// S9 Tab and switch off (REQ-20.2, REQ-1.4): the tab's side and position (app settings), and
// switching the widget off or on for every environment (PUT /api/apps/:appId/widget-state).

import type { WidgetState } from '@feedback/shared/constants/code-lists';
import type { AppView } from '@feedback/shared/contracts/apps';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import { errorText } from '../../lib/admin-api';
import { SettingControl } from '../../settings/setting-control';
import { Badge, Button, ConfirmModal, Loading, Notice, Panel } from '../../ui/primitives';
import { appPath, appSettingsQuery, useAppWrite, useSettingSaver } from '../app-data';

export function TabSection({ app }: { app: AppView }) {
  const settings = useQuery(appSettingsQuery(app.id));
  const saver = useSettingSaver(app.id);
  const [confirming, setConfirming] = useState(false);
  const target: WidgetState = app.widgetState === 'on' ? 'off' : 'on';
  const toggle = useAppWrite(
    app.id,
    (state: WidgetState) => api.put<AppView>(appPath(app.id, '/widget-state'), { state }),
    target === 'off' ? 'The widget is off' : 'The widget is on',
  );
  const side = settings.data?.find((s) => s.code === 'app.tab_side');
  const position = settings.data?.find((s) => s.code === 'app.tab_position');
  const readOnly = !app.can.manageSettings;

  return (
    <div className="flex flex-col gap-4">
      <Panel title="Tab" description="Where the feedback tab sits on the app’s pages.">
        {settings.isPending ? <Loading /> : null}
        {settings.isError ? <Notice tone="danger">{errorText(settings.error)}</Notice> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          {side ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Side</span>
              <SettingControl setting={side} saver={saver} label="Tab side" disabled={readOnly} />
            </div>
          ) : null}
          {position ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Position, % from the top</span>
              <SettingControl setting={position} saver={saver} label="Tab position" disabled={readOnly} />
            </div>
          ) : null}
        </div>
      </Panel>

      <Panel
        title="Switch off"
        description="Switching the widget off hides it on every page of every environment. Tickets stay."
      >
        <p className="text-sm">
          The widget is now{' '}
          {app.widgetState === 'on' ? <Badge tone="success">on</Badge> : <Badge tone="danger">off</Badge>}
        </p>
        {readOnly ? null : (
          <div className="mt-3">
            <Button variant={target === 'off' ? 'danger' : 'primary'} onClick={() => setConfirming(true)}>
              {target === 'off' ? 'Switch the widget off' : 'Switch the widget on'}
            </Button>
          </div>
        )}
      </Panel>

      <ConfirmModal
        open={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) toggle.reset();
        }}
        title={target === 'off' ? 'Switch the widget off?' : 'Switch the widget on?'}
        description={
          target === 'off'
            ? 'Nobody can report from the app’s pages until it’s switched on again.'
            : 'The tab appears again on the app’s pages.'
        }
        confirmLabel={target === 'off' ? 'Switch off' : 'Switch on'}
        danger={target === 'off'}
        busy={toggle.isPending}
        error={toggle.isError ? errorText(toggle.error) : undefined}
        onConfirm={() => toggle.mutate(target, { onSuccess: () => setConfirming(false) })}
      />
    </div>
  );
}
